import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { ListRenderItemInfo, StyleSheet, TouchableOpacity } from 'react-native'
import { Stack } from 'zens-router'
import { Color, FloatingButton, Icon, List, ListItem, Text } from 'zens-storybook'
import * as FileSystem from 'expo-file-system/legacy'
import * as DocumentPicker from 'expo-document-picker'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Color.simplyWhite,
  },
  row: {
    minHeight: 70,
  },
})

interface Props {
  onSelect?: (file: string) => void
}

const FirmwareFile = (props: Props) => {
  const { onSelect } = props
  const { t } = useTranslation()
  const [files, setFiles] = useState<string[]>([])
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    readFiles()
  }, [])

  async function readFiles() {
    const results = await FileSystem.readDirectoryAsync(FileSystem.documentDirectory!)
    setFiles(results.filter((name) => name.endsWith('.zip')))
  }

  async function removeFile(name: string) {
    try {
      await FileSystem.deleteAsync(`${FileSystem.documentDirectory}${name}`)
      await readFiles()
    } catch (error) {
      console.debug('Failed to remove file ', name, ':', error)
    }
  }

  function startUpdate(name: string) {
    onSelect?.(`${FileSystem.documentDirectory}${name}`)
  }

  function renderFile(info: ListRenderItemInfo<string>) {
    const { item } = info
    return (
      <ListItem
        style={styles.row}
        title={item}
        chevron={!editing}
        addon={
          editing
            ? {
                right: (
                  <TouchableOpacity onPress={() => removeFile(item)} testID="FirmwareFile.Remove">
                    <Icon name="trash" />
                  </TouchableOpacity>
                ),
              }
            : undefined
        }
        onPress={() => startUpdate(item)}
        testID="FirmwareFile.File"
      />
    )
  }

  async function addFile() {
    setEditing(false)
    const result = await DocumentPicker.getDocumentAsync()
    if (result?.canceled) return
    const assets = result?.assets

    if (assets) {
      await Promise.all(
        assets.map(async (asset) =>
          FileSystem.copyAsync({
            from: asset.uri,
            to: `${FileSystem.documentDirectory}${asset.name}`,
          }),
        ),
      )
    }
    readFiles()
  }

  return (
    <>
      <Stack.Screen
        options={{
          headerRight: () => (
            <TouchableOpacity onPress={() => setEditing(!editing)} testID="FirmwareFile.Edit">
              <Text level={5}>{editing ? <Trans>Cancel</Trans> : <Trans>Edit</Trans>}</Text>
            </TouchableOpacity>
          ),
        }}
      />
      <List
        data={files.sort((a, b) => a?.localeCompare(b))}
        renderItem={renderFile}
        style={styles.container}
        empty={t('No firmware found')}
      />
      <FloatingButton left="plus" onPress={addFile} testID="FirmwareFile.Add" />
    </>
  )
}

export default FirmwareFile
